import type { NetworkInfo } from "@alch/dx-entities";
import { analyticsEvent, EventPrefix } from "@util/analytics";
import clsx from "clsx";
import { uniqBy } from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import type { Control } from "react-hook-form";
import { SelectField } from "../../components/ui/index.js";
import ComposerAppSelect from "./ComposerAppSelect.js";
import ComposerNetworkSelect from "./ComposerNetworkSelect.js";
import type { ComposerFormState } from "./lib/form.js";
import type { ComposerMethodConfig } from "./lib/methods.js";

interface ComposerMethodSelectProps
  extends React.HTMLAttributes<HTMLDivElement> {
  control: Control<ComposerFormState>;
  methodConfigs: ComposerMethodConfig[];
  networkInfos: NetworkInfo[];
  enableAppSelect?: boolean;
}

const ComposerMethodSelect = ({
  control,
  methodConfigs,
  networkInfos,
  enableAppSelect,
  className,
  ...props
}: ComposerMethodSelectProps) => {
  const [methodSearchQuery, setMethodSearchQuery] = useState("");

  const availableMethods = useMemo(
    () =>
      uniqBy(methodConfigs, "name").map((config) => ({
        label: config.name,
        value: config.name,
      })),
    [methodConfigs],
  );

  const isSearchingMethods = !!methodSearchQuery;

  useEffect(() => {
    if (!isSearchingMethods) return;

    analyticsEvent(`${EventPrefix.Sandbox}: Searched for method`);
  }, [isSearchingMethods]);

  return (
    <div className={clsx("flex flex-col gap-4", className)} {...props}>
      <SelectField
        control={control}
        label="Method"
        name="method"
        onSearch={setMethodSearchQuery}
        placeholder="Select method"
        items={availableMethods}
      />

      {enableAppSelect ? (
        <ComposerAppSelect control={control} name="app" />
      ) : null}

      <ComposerNetworkSelect
        control={control}
        networkInfos={networkInfos}
        methodConfigs={methodConfigs}
      />
    </div>
  );
};

export default ComposerMethodSelect;
